"use client";

import { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  CircularProgress,
  Chip,
  Fade,
  useTheme,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MailOutlineIcon from "@mui/icons-material/MailOutlined";
import { useSearchParams } from "next/navigation";

// Returns the inbox URL + label for well-known email providers.
export const getInboxUrl = (email) => {
  const domain = email?.split("@")?.[1]?.toLowerCase() || "";
  if (domain === "gmail.com" || domain === "googlemail.com")
    return { url: "https://mail.google.com", label: "Open Gmail" };
  if (domain.includes("yahoo."))
    return { url: "https://mail.yahoo.com", label: "Open Yahoo Mail" };
  if (
    [
      "outlook.com",
      "hotmail.com",
      "hotmail.co.uk",
      "live.com",
      "live.co.uk",
      "msn.com",
      "windowslive.com",
    ].includes(domain)
  )
    return { url: "https://outlook.live.com", label: "Open Outlook" };
  if (["icloud.com", "me.com", "mac.com"].includes(domain))
    return { url: "https://www.icloud.com/mail", label: "Open iCloud Mail" };
  if (
    ["proton.me", "protonmail.com", "protonmail.ch", "pm.me"].includes(domain)
  )
    return { url: "https://mail.proton.me", label: "Open Proton Mail" };
  if (domain === "aol.com" || domain === "aol.co.uk")
    return { url: "https://mail.aol.com", label: "Open AOL Mail" };
  if (["gmx.com", "gmx.de", "gmx.net", "gmx.at", "gmx.ch"].includes(domain))
    return { url: "https://www.gmx.com", label: "Open GMX" };
  if (["web.de"].includes(domain))
    return { url: "https://web.de/email", label: "Open Web.de" };
  if (["zoho.com", "zohomail.com"].includes(domain))
    return { url: "https://mail.zoho.com", label: "Open Zoho Mail" };
  if (["fastmail.com", "fastmail.fm", "fastmail.org"].includes(domain))
    return { url: "https://app.fastmail.com", label: "Open Fastmail" };
  if (domain === "hey.com")
    return { url: "https://app.hey.com", label: "Open HEY" };
  if (
    ["tuta.com", "tutanota.com", "tutamail.com", "tutanota.de"].includes(domain)
  )
    return { url: "https://app.tuta.com", label: "Open Tuta" };
  return null;
};

/**
 * EmailAuthSection — self-contained magic-link email sign-in widget.
 */
export default function EmailAuthSection({
  disabled = false,
  isMobile = false,
  callbackUrl: callbackUrlProp,
  onSignInStart,
  lastUsed = false,
}) {
  const theme = useTheme();
  const greyColor = theme.palette.grey?.[350] || theme.palette.grey[500];
  const searchParams = useSearchParams();
  const hadLinkError = searchParams?.get("error") === "CredentialsSignin";

  const [expanded, setExpanded] = useState(hadLinkError);
  const [email, setEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [resendCountdown, setResendCountdown] = useState(0);
  const resendTimerRef = useRef(null);
  const sentEmailRef = useRef("");

  const startResendCooldown = () => {
    setResendCountdown(60);
    clearInterval(resendTimerRef.current);
    resendTimerRef.current = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(resendTimerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => () => clearInterval(resendTimerRef.current), []);

  const resolveCallbackUrl = () => {
    if (callbackUrlProp) return callbackUrlProp;
    const params = new URLSearchParams(window.location.search);
    return params.get("callbackUrl") || "/";
  };

  const handleSend = async (override) => {
    const normalised = (override ?? email).trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalised)) {
      setErrorMsg("Please enter a valid email address.");
      return;
    }
    setIsSending(true);
    setErrorMsg("");
    onSignInStart?.();
    try {
      const res = await fetch("/api/auth/magic-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: normalised,
          callbackUrl: resolveCallbackUrl(),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        setErrorMsg(data?.message || "Couldn't send link. Please try again.");
        return;
      }
      sentEmailRef.current = normalised;
      setSent(true);
      startResendCooldown();
    } catch {
      setErrorMsg("Network error. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  // ── Sent state ─────────────────────────────────────────────────────────────
  if (sent) {
    const inbox = getInboxUrl(sentEmailRef.current);
    return (
      <Box sx={{ mt: 1.5, minHeight: 82 }}>
        <Fade in>
          <Box sx={{ textAlign: isMobile ? "left" : "right" }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              &#10003; Link sent to <strong>{sentEmailRef.current}</strong>
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: isMobile ? "flex-start" : "flex-end",
                gap: 1.5,
                mt: 0.75,
                flexWrap: "wrap",
              }}
            >
              {inbox && (
                <Typography
                  component="a"
                  href={inbox.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  sx={{
                    color: "primary.main",
                    fontSize: "0.82rem",
                    lineHeight: 1,
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {inbox.label} &rarr;
                </Typography>
              )}
              {inbox && (
                <Box
                  sx={{
                    width: "1px",
                    height: "12px",
                    backgroundColor: "divider",
                    flexShrink: 0,
                  }}
                />
              )}
              <Typography
                component="button"
                onClick={
                  resendCountdown === 0
                    ? () => handleSend(sentEmailRef.current)
                    : undefined
                }
                disabled={resendCountdown > 0 || isSending}
                sx={{
                  background: "none",
                  border: "none",
                  cursor: resendCountdown === 0 ? "pointer" : "default",
                  color: resendCountdown === 0 ? greyColor : "text.disabled",
                  fontSize: "0.82rem",
                  lineHeight: 1,
                  p: 0,
                  textDecoration: resendCountdown === 0 ? "underline" : "none",
                  textUnderlineOffset: "2px",
                  transition: "color 0.3s",
                }}
              >
                {resendCountdown > 0
                  ? `Resend in ${resendCountdown}s`
                  : isSending
                  ? "Sending…"
                  : "Resend link"}
              </Typography>
            </Box>
          </Box>
        </Fade>
      </Box>
    );
  }

  // ── Idle state ─────────────────────────────────────────────────────────────
  if (!expanded) {
    if (isMobile) {
      return (
        <Box sx={{ mt: 1.5, minHeight: 82 }}>
          <Button
            variant="outlined"
            size="medium"
            fullWidth
            sx={{
              position: "relative",
              overflow: "visible",
              height: { xs: 56, sm: 56 },
              borderRadius: "7px",
              borderColor: "divider",
              color: "text.secondary",
              fontSize: "1rem",
              textTransform: "none",
              px: 2,
              pl: 3,
              "&:hover": { borderColor: "grey.400", color: "text.primary" },
            }}
            onClick={() => setExpanded(true)}
            disabled={disabled}
          >
            {lastUsed && (
              <Chip
                label="Last used"
                size="small"
                sx={{
                  position: "absolute",
                  top: -11,
                  right: 10,
                  height: 20,
                  fontSize: "0.68rem",
                  zIndex: 2,
                  backgroundColor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              />
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 1.5,
                width: "100%",
                color: "inherit",
              }}
            >
              <MailOutlineIcon sx={{ fontSize: 20, flexShrink: 0 }} />
              <span>Sign in with Email</span>
            </Box>
          </Button>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          mt: 1.5,
          minHeight: 82,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-end",
          width: "100%",
          pt: 1.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 0.75,
          }}
        >
          <Typography
            component="button"
            onClick={() => !disabled && setExpanded(true)}
            sx={{
              background: "none",
              border: "none",
              cursor: disabled ? "default" : "pointer",
              color: greyColor,
              fontSize: "0.9rem",
              mr: "12px",
              p: 0,
              opacity: disabled ? 0.5 : 1,
              textDecoration: "underline",
              textDecorationStyle: "dotted",
              textUnderlineOffset: "3px",
              transition: "color 0.2s",
              "&:hover": disabled ? {} : { color: "text.secondary" },
            }}
          >
            Sign in with email — get a link
          </Typography>
          {lastUsed && (
            <Chip
              label="Last used"
              size="small"
              sx={{ height: 20, fontSize: "0.68rem", mr: "12px" }}
            />
          )}
        </Box>
      </Box>
    );
  }

  // ── Expanded input state ───────────────────────────────────────────────────
  return (
    <Fade in>
      <Box
        sx={{
          mt: 1.5,
          minHeight: 82,
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
        }}
      >
        <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
          <TextField
            size="small"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (errorMsg) setErrorMsg("");
            }}
            disabled={isSending}
            autoFocus
            fullWidth
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.stopPropagation();
                handleSend();
              }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                fontSize: isMobile ? "16px" : "0.9rem",
              },
              "& .MuiOutlinedInput-input": {
                fontSize: isMobile ? "16px" : "0.9rem",
              },
            }}
          />
          <IconButton
            onClick={() => handleSend()}
            disabled={isSending || !email.trim()}
            size="small"
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              flexShrink: 0,
              backgroundColor: "text.primary",
              color: "background.paper",
              "&:hover": { backgroundColor: "text.secondary" },
              "&.Mui-disabled": {
                opacity: 0.35,
                backgroundColor: "text.primary",
                color: "background.paper",
              },
            }}
          >
            {isSending ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <ArrowForwardIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Box>
        {errorMsg && (
          <Typography variant="caption" sx={{ color: "error.main" }}>
            {errorMsg}
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: greyColor }}>
          We&apos;ll send a sign-in link
        </Typography>
      </Box>
    </Fade>
  );
}
